import { useState, useRef, useCallback, useEffect } from "react";
import { toast } from "sonner";

/**
 * useCameraStream — bundles the entire camera lifecycle for the
 * recorder dialog: getUserMedia, facingMode flip, torch toggle, zoom +
 * pinch-to-zoom, tap-to-focus, focus indicator, and auto-start/stop
 * tied to the dialog `open` flag.
 *
 * Lives at /app/frontend/src/components/dashboard/recorder/useCameraStream.js.
 *
 * ⚠️ This hook is gated by the Playwright recorder.spec.js E2E harness.
 * Run `yarn test:e2e recorder` BEFORE AND AFTER any change to this file.
 * iOS Safari + Android Chrome behave very differently from each other
 * for `applyConstraints({ advanced: [{ zoom }] })` and torch — don't
 * trust looking-fine-in-Chromium as proof of correctness on a real device.
 *
 * Inputs:
 *   open               — dialog open flag. Drives auto-start/stop of the camera.
 *   isRecordingRef     — ref-boxed boolean (because useMediaRecorder is
 *                        initialised AFTER this hook in the parent — refs
 *                        are mutable so reads from inside our callbacks see
 *                        the latest value). Used to gate flip-camera (you
 *                        can't change track direction mid-record without
 *                        corrupting the file).
 *   recordPhaseRef     — ref-boxed 'setup' | 'review'. Used to gate
 *                        tap-to-focus to the live preview phase only.
 *                        Parent owns the re-attach-on-phase-change effect
 *                        because the hook itself can't depend on a value
 *                        that lives downstream of it in init order.
 *   liveVideoRef       — DOM ref to the live <video>. Owned by the dialog because
 *                        it's also referenced by JSX, but we set srcObject from
 *                        within the hook on stream acquisition.
 *   stopRecordingRef   — ref-wrapped callback (broken cycle — `stopRecording`
 *                        comes from useMediaRecorder, which itself needs
 *                        setCameraReady from this hook). On dialog close we
 *                        invoke `stopRecordingRef.current()` so any in-flight
 *                        recording teardown still fires.
 *
 * Returns: { mediaStreamRef, cameraReady, setCameraReady, cameraError,
 *            facingMode, videoInputCount, flipBusy,
 *            zoomCaps, zoom, torchSupported, torchOn, focusIndicator,
 *            startCamera, stopCameraStream, flipCamera, applyZoom, toggleTorch,
 *            handlePreviewTap, handleTouchStart, handleTouchMove, handleTouchEnd }
 *
 * `setCameraReady` is exposed because useMediaRecorder needs to flip it
 * to false from inside its `onStop` (the recorder tears down the camera
 * tracks too — we keep both pieces of state aligned).
 */
export function useCameraStream({ open, isRecordingRef, recordPhaseRef, liveVideoRef, stopRecordingRef }) {
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState("");
  // Facing direction. `environment` = back camera (the default on
  // mobile), `user` = selfie. Browsers may downgrade to whichever camera
  // is available — see the constraint builder below.
  const [facingMode, setFacingMode] = useState("environment");
  // Brief lockout window after a flip so the UI doesn't let the user
  // double-tap and start a re-init mid-transition.
  const [flipBusy, setFlipBusy] = useState(false);
  // 0 when not yet enumerated; ≥1 once enumerateDevices() resolves.
  // Used to hide the flip button entirely on single-camera laptops.
  const [videoInputCount, setVideoInputCount] = useState(0);
  // Camera capability reports. `zoomCaps` is { min, max, step } when
  // supported, null otherwise.
  const [zoomCaps, setZoomCaps] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [torchSupported, setTorchSupported] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  // Tap-to-focus indicator { x, y } in CSS pixels relative to the
  // preview, or null.
  const [focusIndicator, setFocusIndicator] = useState(null);

  const mediaStreamRef = useRef(null);
  const pinchStartDistRef = useRef(null);
  const pinchStartZoomRef = useRef(1);

  const stopCameraStream = useCallback(() => {
    // If recording is active, stop it cleanly first — the recorder's
    // onstop handler tears down the camera tracks for us. Wrapped in a
    // ref to break the cycle between this hook and useMediaRecorder.
    try { stopRecordingRef?.current?.(); } catch (_e) { /* ignore */ }
    // Direct teardown for the no-recording case (e.g. user opens the
    // dialog, sees the preview, and bails without ever hitting Start).
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((t) => t.stop());
      mediaStreamRef.current = null;
    }
    setCameraReady(false);
  }, [stopRecordingRef]);

  const startCamera = useCallback(async () => {
    setCameraError("");
    setCameraReady(false);
    if (mediaStreamRef.current) {
      setCameraReady(true);
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError("Your browser doesn't support camera access. Please use Chrome, Edge or Safari.");
      return;
    }
    // Try the requested facingMode first with `ideal` so getUserMedia
    // can fall back gracefully on devices that only have one camera.
    // Some Android Chrome versions are stricter and will reject `exact`
    // — `ideal` is the safer default.
    const buildConstraints = (mode) => ({
      video: { facingMode: { ideal: mode } },
      audio: true,
    });
    try {
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia(buildConstraints(facingMode));
      } catch (_innerErr) {
        // Fallback: drop the facingMode hint entirely and accept any
        // camera. Happens on laptops with one camera + strict drivers.
        stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      }
      mediaStreamRef.current = stream;
      if (liveVideoRef.current) liveVideoRef.current.srcObject = stream;

      // Probe zoom + torch capability + enumerate cameras.
      try {
        const track = stream.getVideoTracks()[0];
        const caps = track?.getCapabilities ? track.getCapabilities() : {};
        if (caps && typeof caps.zoom === "object" && caps.zoom.min != null && caps.zoom.max != null) {
          setZoomCaps({ min: caps.zoom.min, max: caps.zoom.max, step: caps.zoom.step || 0.1 });
          const settings = track.getSettings ? track.getSettings() : {};
          setZoom(typeof settings.zoom === "number" ? settings.zoom : caps.zoom.min);
        } else {
          setZoomCaps(null);
          setZoom(1);
        }
        // Torch is a boolean capability — present means "rear camera
        // has an LED you can control". Front cams never expose it.
        setTorchSupported(!!(caps && caps.torch));
        setTorchOn(false); // always start with torch OFF on stream init
      } catch (_e) { /* zoom/torch are best-effort */ }

      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const cams = devices.filter((d) => d.kind === "videoinput");
        setVideoInputCount(cams.length);
      } catch (_e) { setVideoInputCount(0); }

      setCameraReady(true);
    } catch (err) {
      const name = err?.name || "";
      if (name === "NotAllowedError" || name === "PermissionDeniedError") {
        setCameraError("Camera/mic permission denied. Allow access and try again.");
      } else if (name === "NotFoundError" || name === "DevicesNotFoundError") {
        setCameraError("No camera or microphone found on this device.");
      } else {
        setCameraError("Couldn't start the camera. Try a different browser.");
      }
    }
  }, [facingMode, liveVideoRef]);

  // Flip camera between front (user) and back (environment). Disabled
  // during active recording — swapping cameras mid-recording would
  // invalidate the MediaRecorder's track binding and corrupt the file.
  // We stop the current stream entirely, then re-init startCamera()
  // which picks up the new facingMode via the dependency array. Brief
  // black frame is unavoidable on all browsers (no spec for hot-swapping
  // camera direction).
  const flipCamera = useCallback(async () => {
    if (isRecordingRef?.current) {
      toast.info("Stop recording before switching cameras");
      return;
    }
    // Note: we don't bail on `videoInputCount < 2` — some Android
    // browsers under-report cameras. Better to try and let the OS
    // refuse than to silently disable the button.
    setFlipBusy(true);
    const next = facingMode === "user" ? "environment" : "user";
    // Tear down BEFORE flipping state so startCamera's
    // mediaStreamRef check doesn't short-circuit.
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((t) => t.stop());
      mediaStreamRef.current = null;
    }
    setCameraReady(false);
    setFacingMode(next);
    // startCamera runs via the useEffect on facingMode change below.
    // Small delay before clearing flipBusy gives the new stream time
    // to attach to <video>.
    setTimeout(() => setFlipBusy(false), 600);
  }, [facingMode, isRecordingRef]);

  // Push a new zoom value to the camera track. iOS Safari + Android
  // Chrome accept `applyConstraints({ advanced: [{ zoom }] })`;
  // bare-property form is more compatible across older builds.
  const applyZoom = useCallback(async (next) => {
    setZoom(next);
    try {
      const track = mediaStreamRef.current?.getVideoTracks?.()[0];
      if (!track || !track.applyConstraints) return;
      await track.applyConstraints({ advanced: [{ zoom: next }] });
    } catch (_e) { /* zoom is best-effort */ }
  }, []);

  // Toggle the rear-camera LED (torch / flashlight). Same pattern as
  // zoom. Front cameras never report torch capability so the UI is
  // hidden there.
  const toggleTorch = useCallback(async () => {
    if (!torchSupported) return;
    const next = !torchOn;
    setTorchOn(next);
    try {
      const track = mediaStreamRef.current?.getVideoTracks?.()[0];
      if (!track || !track.applyConstraints) return;
      await track.applyConstraints({ advanced: [{ torch: next }] });
    } catch (_e) {
      // Rollback the optimistic UI flip if the device refused.
      setTorchOn(!next);
    }
  }, [torchOn, torchSupported]);

  // Tap-to-focus on the live preview. iOS Safari 17+ + recent Android
  // Chrome expose `focusMode` + `pointsOfInterest` capabilities on the
  // rear camera. We convert the tap's pixel coords to normalised
  // [0,1] coordinates the spec expects.
  const handlePreviewTap = useCallback(async (e) => {
    if (recordPhaseRef?.current !== "setup") return;
    const target = liveVideoRef.current;
    if (!target) return;
    const rect = target.getBoundingClientRect();
    const clientX = e.touches?.[0]?.clientX ?? e.clientX;
    const clientY = e.touches?.[0]?.clientY ?? e.clientY;
    if (clientX == null || clientY == null) return;
    const x = (clientX - rect.left) / rect.width;
    const y = (clientY - rect.top) / rect.height;
    // Drop the small yellow square the iOS Camera app uses so users get
    // visual confirmation their tap registered, even if the browser
    // silently rejects the focus request.
    setFocusIndicator({ x: clientX - rect.left, y: clientY - rect.top });
    setTimeout(() => setFocusIndicator(null), 700);
    try {
      const track = mediaStreamRef.current?.getVideoTracks?.()[0];
      if (!track || !track.applyConstraints) return;
      const caps = track.getCapabilities ? track.getCapabilities() : {};
      const advanced = [];
      if (Array.isArray(caps.focusMode) && caps.focusMode.includes("single-shot")) {
        advanced.push({ focusMode: "single-shot" });
      }
      if (caps.pointsOfInterest) {
        advanced.push({ pointsOfInterest: [{ x, y }] });
      }
      if (advanced.length) {
        await track.applyConstraints({ advanced });
      }
    } catch (_e2) { /* focus is best-effort */ }
  }, [recordPhaseRef, liveVideoRef]);

  // Pinch-to-zoom on the live preview. Native touch events; only active
  // when zoom capability is present.
  const handleTouchStart = useCallback((e) => {
    if (!zoomCaps) return;
    if (e.touches?.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      pinchStartDistRef.current = Math.hypot(dx, dy);
      pinchStartZoomRef.current = zoom;
    }
  }, [zoomCaps, zoom]);

  const handleTouchMove = useCallback((e) => {
    if (!zoomCaps || pinchStartDistRef.current == null) return;
    if (e.touches?.length !== 2) return;
    const dx = e.touches[0].clientX - e.touches[1].clientX;
    const dy = e.touches[0].clientY - e.touches[1].clientY;
    const dist = Math.hypot(dx, dy);
    const ratio = dist / pinchStartDistRef.current;
    const range = zoomCaps.max - zoomCaps.min;
    // Map a 2x pinch-stretch to ~60% of the available zoom range —
    // feels natural and matches the iOS Camera app's responsiveness.
    const next = Math.max(
      zoomCaps.min,
      Math.min(zoomCaps.max, pinchStartZoomRef.current + (ratio - 1) * range * 0.6),
    );
    // Throttle by skipping if the new value would barely change —
    // applyConstraints is heavier than React state.
    if (Math.abs(next - zoom) > (zoomCaps.step || 0.1) / 2) {
      applyZoom(next);
    }
    e.preventDefault();
  }, [zoomCaps, zoom, applyZoom]);

  const handleTouchEnd = useCallback(() => {
    pinchStartDistRef.current = null;
  }, []);

  // Auto start/stop camera with dialog open state. Re-runs when
  // facingMode flips so the new direction takes effect — startCamera()
  // is a no-op when a stream is already live, so flipCamera() must
  // tear down the stream before changing facingMode (it does).
  useEffect(() => {
    if (!open) {
      stopCameraStream();
      return;
    }
    startCamera();
  }, [open, startCamera, stopCameraStream]);

  // The recordPhase → re-attach-srcObject effect is owned by the parent.
  // We can't subscribe to recordPhase here because useMediaRecorder is
  // initialised AFTER this hook in the parent (only refs survive that
  // init-order gap), and a ref deps array doesn't re-fire useEffect.

  return {
    mediaStreamRef,
    cameraReady, setCameraReady,
    cameraError,
    facingMode, videoInputCount, flipBusy,
    zoomCaps, zoom, torchSupported, torchOn, focusIndicator,
    startCamera, stopCameraStream, flipCamera, applyZoom, toggleTorch,
    handlePreviewTap, handleTouchStart, handleTouchMove, handleTouchEnd,
  };
}